import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../providers/AuthProvider';
import { SignInForm } from '../components/auth/SignInForm';
import { SignUpForm } from '../components/auth/SignUpForm';
import { VerifyForm } from '../components/auth/VerifyForm';

/**
 * The three views of the authentication flow, driven by a small state machine:
 * `signIn → signUp → verify` (Component 7). Sign-in also hosts the federated
 * provider controls; the verify view is reached after a successful sign-up.
 */
type AuthView = 'signIn' | 'signUp' | 'verify';

/**
 * Login page — a `signIn / signUp / verify` view state machine.
 *
 * - Keeps the existing "redirect to `/` when authenticated" behaviour (Req 4.1,
 *   4.5) via the `isAuthenticated` guard.
 * - `signUp` collects email + password with inline validation and, on success,
 *   transitions to `verify` while preserving the entered email and the masked
 *   destination (Req 2.2, 2.3).
 * - `verify` confirms the 6-digit code and, on success, returns the User to
 *   `signIn` with a success indication (Req 2.4).
 *
 * Validates: Requirements 1.1, 1.2, 1.3, 2.2, 2.4, 3.1, 4.1, 4.5, 5.1, 5.2,
 * 10.5, 10.6, 10.7.
 */
export function Login() {
  const { isAuthenticated } = useAuth();
  const [view, setView] = useState<AuthView>('signIn');
  const [pendingEmail, setPendingEmail] = useState('');
  const [destinationMasked, setDestinationMasked] = useState<string | undefined>(
    undefined,
  );
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // If already authenticated, redirect to dashboard (existing behaviour).
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const goToSignUp = () => {
    setSuccessMessage(null);
    setView('signUp');
  };

  const goToSignIn = () => {
    setView('signIn');
  };

  const handleRegistered = (email: string, masked?: string) => {
    setPendingEmail(email);
    setDestinationMasked(masked);
    setSuccessMessage(null);
    setView('verify');
  };

  const handleConfirmed = () => {
    // Return to sign-in with a success indication (Req 2.4).
    setSuccessMessage('Your email is verified. You can now sign in.');
    setView('signIn');
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '1rem',
      }}
    >
      {view === 'signIn' && (
        <SignInForm
          onSwitchToSignUp={goToSignUp}
          successMessage={successMessage}
        />
      )}
      {view === 'signUp' && (
        <SignUpForm
          onSwitchToSignIn={goToSignIn}
          onRegistered={handleRegistered}
        />
      )}
      {view === 'verify' && (
        <VerifyForm
          pendingEmail={pendingEmail}
          destinationMasked={destinationMasked}
          onConfirmed={handleConfirmed}
        />
      )}
    </div>
  );
}
